import type { PublicTrafficSource, PublicTrafficSurface } from '@seaa/shared'
import type { SeaaBootstrap } from './app-runtime'
import { analyticsAllowed, clearAnalyticsVisitorCookie, subscribeAnalyticsConsent } from './analytics-consent'

export function publicTrafficSurface(pathname: string, merchantSite: boolean): PublicTrafficSurface | null {
  if (/^\/book\/[^/]+\/?$/.test(pathname)) return 'booking'
  if (merchantSite) return pathname === '/' || pathname === '/links' ? 'storefront' : null
  return ['/', '/examples', '/privacy', '/terms'].includes(pathname) || /^\/for\/[a-z0-9-]+$/.test(pathname) ? 'marketing' : null
}

export function publicTrafficSource(referrer: string, ownOrigin?: string): PublicTrafficSource {
  if (!referrer) return 'direct'
  try {
    const parsed = new URL(referrer)
    if (parsed.origin === ownOrigin) return 'direct'
    const host = parsed.hostname.toLowerCase()
    if (/(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com)$/.test(host)) return 'search'
    if (/(^|\.)(instagram\.com|facebook\.com|tiktok\.com|snapchat\.com|twitter\.com|x\.com|t\.co|linkedin\.com)$/.test(host)) return 'social'
    return 'referral'
  } catch { return 'direct' }
}

let memoryVisitor: { day: string; id: string } | undefined
subscribeAnalyticsConsent(() => { if (!analyticsAllowed()) { memoryVisitor = undefined; clearAnalyticsVisitorCookie() } })
export function dailyPublicVisitor(now = new Date()) {
  if (!analyticsAllowed()) return null
  const day = new Date(now.getTime() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10)
  if (memoryVisitor?.day === day) return memoryVisitor.id
  try {
    const stored = document.cookie.split('; ').find((value) => value.startsWith('seaa_public_visitor_day='))?.split('=')[1]
    const [storedDay, id] = (stored ?? '').split('.')
    if (storedDay === day && id && /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(id)) {
      memoryVisitor = { day, id }
      return id
    }
  } catch { /* Storage restrictions do not prevent the page from working. */ }
  memoryVisitor = { day, id: crypto.randomUUID() }
  const remainingSeconds = Math.max(1, Math.ceil((Date.parse(`${day}T00:00:00+03:00`) + 86_400_000 - now.getTime()) / 1000))
  try { document.cookie = `seaa_public_visitor_day=${day}.${memoryVisitor.id}; Max-Age=${remainingSeconds}; Path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}` } catch { /* Use this page's in-memory identifier. */ }
  return memoryVisitor.id
}

export function createPublicTrafficTracker(bootstrap: SeaaBootstrap) {
  let previousNavigation = ''
  return () => {
    if (!import.meta.env.PROD || import.meta.env.MODE === 'test' || document.visibilityState !== 'visible') return
    if (!analyticsAllowed()) { previousNavigation = ''; memoryVisitor = undefined; clearAnalyticsVisitorCookie(); return }
    const customerOrigin = bootstrap.customerAppOrigin || bootstrap.origin
    const platformHost = window.location.origin === customerOrigin
    if (!platformHost && !bootstrap.site) return
    const surface = publicTrafficSurface(window.location.pathname, Boolean(bootstrap.site))
    if (!surface) { previousNavigation = ''; return }
    // Only pathname + language stay in local memory. Neither is sent to the API.
    const navigation = `${window.location.pathname}:${new URLSearchParams(window.location.search).get('lang') ?? bootstrap.locale}`
    if (navigation === previousNavigation) return
    previousNavigation = navigation
    void fetch('/api/traffic', {
      method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', keepalive: true, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId: crypto.randomUUID(), visitorId: dailyPublicVisitor(), surface, source: publicTrafficSource(document.referrer, window.location.origin) }),
    }).catch(() => { /* Analytics failure never blocks a public journey. */ })
  }
}
